import {
  COMMUNITY_DIRECT_LINK_TOOLTIPS,
  COMMUNITY_DIRECT_LINK_URL_PREFIX,
  COMMUNITY_INTERACTION_TYPE_DATA,
  COMMUNITY_INTERACTION_TYPES,
} from '@/app/community/constants';
import { mediaSourceType } from '@/config';
import { formatNumber, localize } from '@/utils/formatters';

export const formatLargeCount = (count) => {
  const formatter = Intl.NumberFormat('en', {
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return formatter.format(count);
};

export const formatCount = (
  count,
  singularSuffix = 'Like',
  pluralSuffix = 'Likes',
  threshold = 99999,
) => {
  const parsed = count ? parseInt(count, 10) : 0;
  const suffix = parsed === 1 ? singularSuffix : pluralSuffix;
  if (parsed > threshold) {
    return `${formatLargeCount(parsed)} ${suffix}`;
  }
  return `${localize(parsed, false)} ${suffix}`;
};

const getStatChips = (media) => {
  if (!media) return [];
  const { source = '', sourceData = {} } = media ?? {};
  const chips = [];
  if (source.includes(mediaSourceType.FACEBOOK)) {
    chips.push({
      label: formatCount(sourceData?.reactions, 'Reaction', 'Reactions'),
      icon: 'heart',
    });
  } else if (source.includes(mediaSourceType.INSTAGRAM)) {
    chips.push({
      label: formatCount(sourceData?.likeCount, 'Like', 'Likes'),
      icon: 'heart',
    });
  } else {
    chips.push({
      label: formatCount(sourceData?.likes, 'Like', 'Likes'),
      icon: source.includes(mediaSourceType.YOUTUBE) ? 'thumbs-up' : 'heart',
    });
  }
  if (source.includes(mediaSourceType.TWITTER)) {
    const tweetStats = [
      {
        label: formatCount(sourceData?.retweets, 'Repost', 'Reposts'),
        icon: 'retweet',
      },
      {
        label: formatCount(sourceData?.quoteTweets, 'Quote Post', 'Quote Posts'),
        icon: 'retweet',
      },
    ];
    chips.unshift(...tweetStats);
  }
  if (source.includes(mediaSourceType.YOUTUBE)) {
    chips.push({
      label: formatCount(sourceData?.dislikes, 'Dislike', 'Dislikes'),
      icon: 'thumbs-down',
    });
  }
  return chips;
};

const getTargetCaption = (media) => {
  const { source = '', sourceData = {} } = media ?? {};
  if (source.includes(mediaSourceType.FACEBOOK)) {
    return sourceData?.message;
  }
  if (source.includes(mediaSourceType.TWITTER)) {
    return sourceData?.text;
  }
  if (source.includes(mediaSourceType.YOUTUBE)) {
    return sourceData?.title;
  }
  return sourceData?.caption;
};

const getAspectRatioFromUrl = (url, defaultRatio = 1.0) => {
  if (!url) return undefined;
  const w = url?.match(/w=([^&]*)/)?.[1];
  const h = url?.match(/h=([^&]*)/)?.[1];
  if (!w || !h) {
    return defaultRatio;
  }
  if (w && h) {
    return Number(formatNumber(w / h, false, undefined, 2));
  }
  return undefined;
};

const getAccountHandle = (media) => {
  const { accountDetails, instagramUser, pageDetails } = media?.sourceData ?? {};
  return (
    instagramUser?.handle ??
    pageDetails?.name ??
    accountDetails?.handle ??
    accountDetails?.tiktokUsername ??
    accountDetails?.title
  );
};

const getPostSourceLink = (media) => {
  const { sourceData = {} } = media ?? {};
  return (
    sourceData?.url ?? // Instagram, Facebook
    sourceData?.shareUrl ?? // TikTok
    sourceData?.videoLink ?? // TikTok
    sourceData?.youtubeLink ?? // YouTube
    sourceData?.permalinkUrl // Twitter Mention
  );
};

const getPostSourceLinkTooltip = (comment) => {
  const adLinkTooltip = comment.isAdComment
    ? '. If an ad is inactive only users with access to the connected ad account will be able to see the preview.'
    : '';
  const platform = COMMUNITY_INTERACTION_TYPE_DATA[comment.type].label;
  return `See on ${platform}${adLinkTooltip}`;
};

const getPostThumbnailUrl = (media, vertical) => {
  const { imageSizes, sourceData } = media ?? {};
  if (vertical) {
    return imageSizes?.small?.url ?? sourceData?.imageUrl;
  }
  return sourceData?.urls?.thumbs ?? imageSizes?.smallSquare?.url ?? sourceData?.imageUrl;
};

const getUgcCommentSourceLink = (media, comment) => {
  const { sourceData = {} } = media ?? {};
  if (comment.type === COMMUNITY_INTERACTION_TYPES.TWITTER_RETWEET) {
    const { retweetedStatus } = comment ?? {};
    const handle = retweetedStatus?.screenName ?? retweetedStatus?.authorUsername;
    const tweetId = retweetedStatus?.tweetId ?? retweetedStatus?.twitterTweetId;
    if (handle && tweetId) {
      return `https://twitter.com/${handle}/status/${tweetId}`;
    }
    return sourceData?.permalinkUrl;
  }
  if (
    comment.type === COMMUNITY_INTERACTION_TYPES.TWITTER_QUOTE ||
    comment.type === COMMUNITY_INTERACTION_TYPES.TWITTER_MENTION
  ) {
    const { twitterTweetId, authorUserId } = comment ?? {};
    if (twitterTweetId && authorUserId) {
      return `https://twitter.com/${authorUserId}/status/${twitterTweetId}`;
    }
    return undefined;
  }
  return undefined;
};

const getCommentSourceLink = (comment, media, activeComment) => {
  const prefix = COMMUNITY_DIRECT_LINK_URL_PREFIX[comment.type];
  if (comment?.type === COMMUNITY_INTERACTION_TYPES.TWITTER_MENTION) {
    return `${prefix}${comment.username}/status/${comment.twitterTweetId}`;
  }
  if (comment?.type === COMMUNITY_INTERACTION_TYPES.FACEBOOK_COMMENT) {
    return `${prefix}${comment.facebookCommentId}`;
  }
  if (comment?.type === COMMUNITY_INTERACTION_TYPES.TIKTOK_COMMENT) {
    const videoId = media?.sourceId ?? activeComment?.videoId;
    return videoId ? `${prefix}${comment.tiktokCommentId}&video_id=${videoId}` : undefined;
  }
  if (comment?.type === COMMUNITY_INTERACTION_TYPES.YOUTUBE_COMMENT) {
    const sourceLink = media?.sourceData?.youtubeLink;
    if (!comment.isDeleted && !media.status !== 1 && sourceLink) {
      return `${sourceLink}&lc=${comment.youtubeCommentId}`;
    }
    return undefined;
  }
  return undefined;
};

const getCommentSourceLinkTooltip = (comment, ugc) => {
  if (ugc) {
    return getPostSourceLinkTooltip(comment);
  }
  if (comment?.type === COMMUNITY_INTERACTION_TYPES.FACEBOOK_COMMENT && comment?.isAdComment) {
    return COMMUNITY_DIRECT_LINK_TOOLTIPS.FACEBOOK_ADS_COMMENT;
  }
  return COMMUNITY_DIRECT_LINK_TOOLTIPS[comment?.type];
};

export {
  getAccountHandle,
  getAspectRatioFromUrl,
  getCommentSourceLink,
  getCommentSourceLinkTooltip,
  getPostSourceLink,
  getPostSourceLinkTooltip,
  getPostThumbnailUrl,
  getStatChips,
  getTargetCaption,
  getUgcCommentSourceLink,
};
